import {
  DaySlots,
  TimeSlot,
} from '@fingertip/creator-proto/gen/fingertip/creator/event_type/v1/event_type_pb'
import {
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@fingertip/icons'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isAfter,
  isSameMonth,
  isToday,
  startOfMonth,
} from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { observer } from 'mobx-react-lite'
import React, { type JSX, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { getWeekDays, yyyymmdd } from '@/components/booking/utils'
import { TimeZonePicker } from '@/components/shared/time-zone-picker'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { RootStoreContext } from '@/lib/stores/root-store'
import { cn } from '@/lib/utils/cn'

type OverlappingCirclesProps = {
  count: number
  color?: string
  className?: string
}

const OverlappingCircles = ({
  color,
  count,
  className,
}: OverlappingCirclesProps) => {
  // Ensure count is within the valid range
  const validCount = Math.max(0, Math.min(count, 5))

  const circles = Array.from({ length: validCount }, (_, index) => (
    <div
      key={index}
      className="absolute size-[6px] rounded-full border border-white"
      style={{
        left: `${index * 4}px`,
        backgroundColor: color ? color : 'black',
      }} // Adjust the overlap by changing the left value
    />
  ))

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <div className="relative" style={{ width: `${validCount * 4 + 2}px` }}>
        {circles}
      </div>
    </div>
  )
}

export type DatePickerProps = {
  /** which day of the week to render the calendar. Usually Sunday (=0) or Monday (=1) - default: Sunday */
  weekStart?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  /** Fires whenever a selected date is changed. */
  onChange: (date: Date | null) => void
  /** Fires when the month is changed. */
  onMonthChange?: (date: Date) => void
  /** which date or dates are currently selected (not tracked from here) */
  selected?: Date | Date[] | null
  /** defaults to current date. */
  minDate?: Date
  /** Furthest date selectable in the future, default = UNLIMITED */
  maxDate?: Date
  /** locale, any IETF language tag, e.g. "hu-HU" - defaults to Browser settings */
  locale?: string
  /** Defaults to [], which dates are not bookable. Array of valid dates like: ["2022-04-23", "2022-04-24"] */
  excludedDates?: string[]
  /** defaults to all, which dates are bookable (inverse of excludedDates) */
  includedDates?: string[]
  /** allows adding classes to the container */
  className?: string
  /** Shows a small loading spinner next to the month name */
  isLoading?: boolean
  /** used to query the multiple selected dates */
  eventSlug?: string
  /** used to store some info about the day */
  slotsByDay?: { [key: string]: DaySlots }
  /** Color of the dots to indicate slots */
  slotColor?: string
  /** Controls logic if this uses groupClass */
  usesGroupClass?: boolean
}

export const Day = ({
  date,
  active,
  disabled,
  timeSlots,
  slotColor,
  ...props
}: JSX.IntrinsicElements['button'] & {
  active: boolean
  date: Date
  timeSlots?: TimeSlot[]
  slotColor?: string
}) => {
  const dotCount = useMemo(() => {
    if (!timeSlots || timeSlots.length === 0) return 0
    return timeSlots.reduce((count, timeSlot) => {
      return timeSlot.groupClass &&
        (timeSlot.groupClass.available > 0 ||
          timeSlot.groupClass.waitlistEnabled)
        ? count + 1
        : count
    }, 0)
  }, [timeSlots])

  return (
    <button
      type="button"
      className={cn(
        'disabled:text-bookinglighter cursor-pointer absolute bottom-0 left-0 right-0 top-0 mx-auto w-full rounded-md border-2 border-transparent text-center text-sm font-medium disabled:cursor-default disabled:border-transparent disabled:font-light ',
        active
          ? 'bg-muted text-brand'
          : !disabled
            ? ' hover:border-brand-default bg-emphasis'
            : 'opacity-30',
      )}
      data-testid="day"
      data-disabled={disabled}
      disabled={disabled}
      {...props}
    >
      <div>
        {format(date, 'd')}
        {isToday(date) && (
          <span
            className={cn(
              'bg-muted absolute left-1/2 top-1/2 flex h-[5px] w-[5px] -translate-x-1/2 translate-y-[8px] items-center justify-center rounded-full align-middle sm:translate-y-[12px]',
              active && 'invert',
            )}
          >
            <span className="sr-only">Today</span>
          </span>
        )}
      </div>
      <OverlappingCircles
        count={dotCount}
        className="h-[3px] pt-[2px]"
        color={slotColor}
      />
    </button>
  )
}

const NoAvailabilityOverlay = ({
  month,
  nextMonthButton,
}: {
  month: Date
  nextMonthButton: () => void
}) => {
  const { t } = useTranslation()
  return (
    <div className=" absolute left-1/2 top-40 -mt-10 w-max -translate-x-1/2 -translate-y-1/2 rounded-md border border-muted bg-muted p-8 shadow-sm">
      <h4 className=" mb-4 font-medium">
        {t('no_availability_in')} {format(month, 'MMM')}
      </h4>
      <Button onClick={nextMonthButton} type="button">
        <span className="mr-2">{t('view_next_month')}</span>
        <ArrowRightIcon />
      </Button>
    </div>
  )
}

const Days = ({
  minDate = toZonedTime(new Date(), 'Etc/UTC'),
  excludedDates = [],
  weekStart,
  DayComponent = Day,
  selected,
  month,
  nextMonthButton,
  usesGroupClass,
  ...props
}: Omit<DatePickerProps, 'locale' | 'className' | 'weekStart'> & {
  DayComponent?: React.FC<React.ComponentProps<typeof Day>>
  month: Date
  weekStart: number
  nextMonthButton: () => void
}) => {
  const firstOfMonth = startOfMonth(month)
  const lastOfMonth = endOfMonth(month)
  const weekdayOfFirst = getDay(firstOfMonth)

  const availableDates = (includedDates: string[] | undefined) => {
    const interval = { start: firstOfMonth, end: lastOfMonth }
    const dates = eachDayOfInterval(interval).map((date) => yyyymmdd(date))
    return includedDates
      ? dates.filter((date) => includedDates.includes(date))
      : dates
  }

  const includedDates = isSameMonth(minDate, month)
    ? availableDates(props.includedDates)
    : props.includedDates

  const days = Array((weekdayOfFirst - weekStart + 7) % 7).fill(null)
  for (let date of eachDayOfInterval({
    start: firstOfMonth,
    end: lastOfMonth,
  })) {
    days.push(date)
  }

  const isActive = (day: Date) => {
    // for selecting a range of dates
    if (Array.isArray(selected)) {
      return (
        Array.isArray(selected) &&
        selected?.some((e) => yyyymmdd(e) === yyyymmdd(day))
      )
    }

    if (selected && yyyymmdd(selected) === yyyymmdd(day)) {
      return true
    }

    return false
  }

  const getTimeSlots = (day: Date): TimeSlot[] => {
    const dayKey = yyyymmdd(day)
    if (
      props.slotsByDay &&
      props.slotsByDay[dayKey] &&
      props.slotsByDay[dayKey].slots
    )
      return props.slotsByDay[dayKey].slots
    return []
  }

  const daysToRenderForTheMonth = days.map((day) => {
    if (!day) return { day: null, disabled: true }

    const timeSlots = getTimeSlots(day).filter((timeSlot) => {
      return (
        timeSlot.groupClass &&
        (timeSlot.groupClass.available > 0 ||
          timeSlot.groupClass.waitlistEnabled)
      )
    })

    return {
      day: day,
      timeSlots: timeSlots,
      disabled:
        (!!usesGroupClass && timeSlots.length === 0) ||
        (includedDates && !includedDates.includes(yyyymmdd(day))) ||
        excludedDates.includes(yyyymmdd(day)),
    }
  })

  /**
   * Takes care of selecting a valid date in the month if the selected date is not available in the month
   */

  const useHandleInitialDateSelection = () => {
    // Let's not do something for now in case of multiple selected dates as behaviour is unclear and it's not needed at the moment
    if (selected instanceof Array) {
      return
    }
    const firstAvailableDateOfTheMonth = daysToRenderForTheMonth.find(
      (day) => !day.disabled,
    )?.day

    const isSelectedDateAvailable = selected
      ? daysToRenderForTheMonth.some(({ day, disabled }) => {
          if (day && yyyymmdd(day) === yyyymmdd(selected) && !disabled)
            return true
        })
      : false

    if (!isSelectedDateAvailable && firstAvailableDateOfTheMonth) {
      // If selected date not available in the month, select the first available date of the month
      // props?.onChange(firstAvailableDateOfTheMonth) // TODO
    }

    if (!firstAvailableDateOfTheMonth) {
      // props?.onChange(null) //TODO
    }
  }

  useEffect(useHandleInitialDateSelection)

  return (
    <>
      {daysToRenderForTheMonth.map(({ day, disabled, timeSlots }, idx) => {
        return (
          <div
            key={day === null ? `e-${idx}` : `day-${format(day, 'yyyy-MM-dd')}`}
            className="relative w-full pt-[100%]"
          >
            {day === null ? (
              <div key={`e-${idx}`} />
            ) : props.isLoading ? (
              <button
                className="absolute inset-0 mx-auto cursor-pointer flex w-full items-center justify-center rounded-xs border-transparent bg-muted text-center font-medium text-muted-foreground opacity-50"
                key={`e-${idx}`}
                disabled
              >
                <Skeleton className="h-4 w-5" />
              </button>
            ) : (
              <DayComponent
                date={day}
                timeSlots={timeSlots}
                onClick={() => {
                  props.onChange(day)
                }}
                disabled={disabled}
                active={isActive(day)}
                slotColor={props.slotColor}
              />
            )}
          </div>
        )
      })}

      {!props.isLoading && includedDates && includedDates?.length === 0 && (
        <NoAvailabilityOverlay
          month={month}
          nextMonthButton={nextMonthButton}
        />
      )}
    </>
  )
}

export const BookerStageDatePicker = observer(
  ({
    weekStart = 0,
    className,
    selected,
    onMonthChange,
    ...passThroughProps
  }: DatePickerProps & Partial<React.ComponentProps<typeof Days>>) => {
    const {
      bookerStore: { month },
    } = useContext(RootStoreContext)

    const changeMonth = (newMonth: number) => {
      if (onMonthChange) {
        onMonthChange(addMonths(month, newMonth))
      }
    }

    return (
      <>
        <div className={className}>
          <div className="mb-1 flex items-center justify-between text-xl">
            <span className="text-base text-muted-foreground">
              {month ? (
                <span>
                  {format(month, 'LLLL')} {format(month, 'yyyy')}
                </span>
              ) : (
                <Skeleton className="h-8 w-24" />
              )}
            </span>

            <div>
              <div className="flex space-x-1">
                <Button
                  type="button"
                  className="group size-8 p-1"
                  onClick={() => changeMonth(-1)}
                  disabled={!isAfter(month, new Date())}
                  data-testid="decrementMonth"
                  variant="muted"
                  size="sm"
                >
                  <ChevronLeftIcon />
                </Button>

                <Button
                  type="button"
                  className="group size-8 p-1"
                  onClick={() => changeMonth(+1)}
                  data-testid="incrementMonth"
                  variant="muted"
                  size="sm"
                >
                  <ChevronRightIcon />
                </Button>
              </div>
            </div>
          </div>

          <div className="mb-2 grid grid-cols-7 gap-4 border-y border-muted text-center md:mb-0 md:border-0">
            {getWeekDays('short').map((weekDay) => (
              <div
                key={weekDay}
                className="my-4 text-xs font-medium uppercase tracking-widest"
              >
                {weekDay}
              </div>
            ))}
          </div>

          <div className="relative grid grid-cols-7 gap-1 text-center">
            <Days
              weekStart={weekStart}
              selected={selected}
              {...passThroughProps}
              month={month}
              nextMonthButton={() => changeMonth(+1)}
            />
          </div>
        </div>

        <TimeZonePicker />
      </>
    )
  },
)
